import { useEffect, useRef } from 'react';
import './Vortex.css';

interface VortexProps {
  isActive?: boolean;
  intensity?: number;
}

export function Vortex({ isActive = false, intensity = 1 }: VortexProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);

    let time = 0;
    const particles: Array<{
      angle: number;
      radius: number;
      speed: number;
      size: number;
      hue: number;
    }> = [];

    // Create particles
    for (let i = 0; i < 200; i++) {
      particles.push({
        angle: Math.random() * Math.PI * 2,
        radius: 50 + Math.random() * 150,
        speed: 0.5 + Math.random() * 1.5,
        size: 1 + Math.random() * 2,
        hue: 25 + Math.random() * 20, // Orange-gold range
      });
    }

    const animate = () => {
      const rect = canvas.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Clear with fade effect
      ctx.fillStyle = 'rgba(10, 10, 11, 0.15)';
      ctx.fillRect(0, 0, rect.width, rect.height);

      // Draw vortex center (black hole)
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, 80
      );
      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.9)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 80, 0, Math.PI * 2);
      ctx.fill();

      // Draw particles
      const activeMultiplier = isActive ? 2 : 1;
      particles.forEach((p) => {
        p.angle += (p.speed * 0.02 * activeMultiplier * intensity);
        
        // Spiral inward slowly when active
        if (isActive && p.radius > 60) {
          p.radius -= 0.1;
        } else if (!isActive && p.radius < 150) {
          p.radius += 0.05;
        }

        const x = centerX + Math.cos(p.angle) * p.radius;
        const y = centerY + Math.sin(p.angle) * p.radius;

        // Color based on distance from center
        const distRatio = p.radius / 150;
        const alpha = 0.3 + distRatio * 0.7;
        const lightness = 50 + (1 - distRatio) * 30;
        
        ctx.fillStyle = `hsla(${p.hue}, 100%, ${lightness}%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(x, y, p.size * (isActive ? 1.5 : 1), 0, Math.PI * 2);
        ctx.fill();

        // Draw trail
        const trailLength = isActive ? 8 : 4;
        for (let t = 1; t <= trailLength; t++) {
          const trailAngle = p.angle - (p.speed * 0.02 * t * 0.5);
          const trailX = centerX + Math.cos(trailAngle) * p.radius;
          const trailY = centerY + Math.sin(trailAngle) * p.radius;
          ctx.fillStyle = `hsla(${p.hue}, 100%, ${lightness}%, ${alpha * (1 - t / trailLength) * 0.5})`;
          ctx.beginPath();
          ctx.arc(trailX, trailY, p.size * 0.5, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // Outer glow ring
      const glowGradient = ctx.createRadialGradient(
        centerX, centerY, 120,
        centerX, centerY, 200
      );
      glowGradient.addColorStop(0, `rgba(255, 140, 0, ${isActive ? 0.15 : 0.05})`);
      glowGradient.addColorStop(1, 'rgba(255, 140, 0, 0)');
      ctx.fillStyle = glowGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 200, 0, Math.PI * 2);
      ctx.fill();

      time += 0.016;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [isActive, intensity]);

  return (
    <div className="vortex-container">
      <canvas ref={canvasRef} className="vortex-canvas" />
      <div className="vortex-overlay" />
    </div>
  );
}







